import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, of, switchMap, take } from 'rxjs';
import { CompanyService } from 'app/services/company.service';
import { AccessApprovalService } from 'app/services/access-approval.service';

/**
 * Bloqueia a entrada em rotas operacionais enquanto o pedido de aprovação de
 * entrada por médico remoto (ver `AccessApprovalService`) não é ALLOWED.
 * Redireciona de fato para uma rota dedicada (não overlay) — a pessoa não
 * deve conseguir interagir com telas operacionais nesse meio tempo.
 *
 * Cravado em TODAS as rotas autenticadas de topo (`app.routes.ts`), não só
 * `home` — a pessoa bloqueada não tem menu pra navegar dentro do app, então o
 * caminho real de tentar acessar outra tela é link direto/deep link/aba nova,
 * não navegação por dentro. Um guard reativo no componente raiz (que só age
 * depois que a rota já montou) não fecha essa brecha; só um `canActivate`
 * avaliado ANTES do componente montar fecha de verdade.
 *
 * Cache-first: se já sabemos que está ALLOWED ou bloqueado (via
 * `AccessApprovalService.state$`, mantido vivo por push de socket), decide na
 * hora sem round-trip. Só faz a checagem ativa (HTTP) quando o estado ainda é
 * desconhecido — primeiro carregamento da sessão / link direto.
 */
export const AccessApprovalGuard: CanActivateFn = () => {
    const router = inject(Router);
    const companyService = inject(CompanyService);
    const accessApprovalService = inject(AccessApprovalService);

    const cached = accessApprovalService.state$.value;
    if (cached) {
        return cached.status === 'ALLOWED' ? true : router.parseUrl('/access-approval/status');
    }

    return companyService.currentCompany$.pipe(
        take(1),
        switchMap((company) => {
            if (!company?.company_id) return of(true);
            return accessApprovalService.checkCompanyAccess(company.company_id).pipe(
                map((result) => (result.status === 'ALLOWED' ? true : router.parseUrl('/access-approval/status'))),
            );
        }),
    );
};
